import React, { useCallback, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Alert, Column, Heading, Row } from '@chargify/custom-ui-components';
import { useActor, useSelector } from '@xstate/react';
import { GlobalStateContext } from '../../contexts';
import { subscriptionsSelectors } from '../../machines';
import { customFieldsSelectors } from '../../machines/customFields/selectors';
import CustomFieldsPreview from '../../components/CustomFieldsPreview';

export type Props = {
  /**
   * Subscription identifier
   */
  subscriptionId: number;
  /**
   * Product catalog identifier
   */
  catalogId: number;
};

const CustomFieldsContainer = ({ catalogId, subscriptionId }: Props) => {
  const { subscriptionsService } = useContext(GlobalStateContext);
  const { t } = useTranslation(['subscriptions-manager']);
  const navigate = useNavigate();

  const { customFieldsRef } = useSelector(
    subscriptionsService,
    useCallback(
      subscriptionsSelectors.getSubscriptionById(catalogId, subscriptionId),
      []
    )
  );

  const [customFieldsState, sendToMachine] = useActor(customFieldsRef);

  useEffect(() => {
    if (customFieldsState.matches('idle')) {
      sendToMachine({ type: 'INITIALIZE' });
    }
  }, []);

  const customFields = useSelector(
    customFieldsRef,
    customFieldsSelectors.getSubscriptionCustomFields
  );

  const { isFetchLoading, isFetchError } = useSelector(
    customFieldsRef,
    customFieldsSelectors.getSubscriptionCustomFieldsState
  );

  return (
    <>
      <Row>
        <Column span={[24, 7]}>
          <Heading variant="h4">
            {t('custom_fields.additional_settings')}
          </Heading>
        </Column>
        <Column span={[24, 17]}>
          {isFetchLoading && <CustomFieldsPreview.LoadingSkeleton />}
          {customFields && (
            <CustomFieldsPreview
              customFields={customFields}
              onEdit={() => {
                sendToMachine({ type: 'EDIT_CUSTOM_FIELDS' });
                navigate(`/customFields/${catalogId}/${subscriptionId}`);
              }}
            />
          )}
          {isFetchError && (
            <Alert color="danger">
              <Alert.TextMessage>
                {t(
                  'custom_fields.we_were_unable_to_display_your_custom_fields'
                )}
              </Alert.TextMessage>
            </Alert>
          )}
        </Column>
      </Row>
    </>
  );
};

export default CustomFieldsContainer;
